import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Chip,
    IconButton,
    Card,
    Button,
    Skeleton,
    Alert,
    Select,
    MenuItem,
    Stack,
    Box,
    Typography,
    Tooltip,
    useTokens,
} from '@barracuda-internal/bds-core';
import { useOpenScan } from './hooks/useOpenScan';
import { useScanFilters } from './hooks/useScanFilters';
import { formatTimestamp } from '../../../common/utils/formatTimestamp';
import { CATEGORY_LABEL } from './findings/categoryToScanner';
import { fetchScans } from './scansReducer';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COLUMNS = [
    { key: 'time',     label: 'Time',     width: 150 },
    { key: 'corr',     label: 'Corr. ID', width: 160 },
    { key: 'apiKey',   label: 'API Key',  minWidth: 100, maxWidth: 140 },
    { key: 'result',   label: 'Result',   width: 90 },
    { key: 'findings', label: 'Findings', minWidth: 240 },
    { key: 'latency',  label: 'Latency',  width: 80,  align: 'right' },
    { key: 'content',  label: 'Content',  width: 120 },
];

const RESULT_BDSTYPE = {
    allow:   'staticSuccess',
    block:   'staticError',
    partial: 'staticWarning',
};

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function humanizeMs(ms) {
    if (ms == null) return '—';
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${ms}ms`;
}

// ---------------------------------------------------------------------------
// PaginationFooter (Task 20)
// Uses BDS Select + IconButton for testability (getByLabelText / getByRole).
// ---------------------------------------------------------------------------

function PaginationFooter({ count }) {
    const tokens = useTokens();
    const { filters, setFilter } = useScanFilters();
    const { page, pageSize } = filters;
    const totalPages = Math.max(1, Math.ceil(count / pageSize));
    const from = count === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, count);

    return (
        <Stack
            direction="row"
            sx={{
                alignItems: 'center',
                gap: tokens.aliasesSpaceComponentDefault,
                padding: tokens.aliasesSpaceContainerDense,
                borderTop: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
            }}
        >
            {/* Rows per page */}
            <Typography variant="captionNeutral">Rows per page:</Typography>
            <Select
                bdsSize="small"
                value={pageSize}
                onChange={(e) => {
                    setFilter('pageSize', Number(e.target.value));
                    setFilter('page', 1);
                }}
                inputProps={{ 'aria-label': 'Rows per page' }}
                MenuProps={{ PaperProps: { sx: { backgroundColor: tokens.aliasesColorBgStaticContainerSubtle, color: tokens.aliasesColorTextStaticEmphasis } } }}
                sx={{ width: 80, minWidth: 80, flexShrink: 0 }}
            >
                {[25, 50, 100, 200].map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
            </Select>

            {/* Range text */}
            <Typography variant="captionNeutral">
                {`${from}–${to} of ${count.toLocaleString()}`}
            </Typography>

            <Box sx={{ flex: 1 }} />

            {/* Previous page */}
            <IconButton
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setFilter('page', page - 1)}
            >
                ◀
            </IconButton>

            <Typography variant="captionNeutral">
                Page {page} of {totalPages}
            </Typography>

            {/* Next page */}
            <IconButton
                aria-label="Next page"
                disabled={page >= totalPages}
                onClick={() => setFilter('page', page + 1)}
            >
                ▶
            </IconButton>
        </Stack>
    );
}

// ---------------------------------------------------------------------------
// ScanList (Tasks 19 + 20)
// ---------------------------------------------------------------------------

export default function ScanList() {
    const dispatch = useDispatch();

    // Handle both test store shape (flat) and real app shape (guardian.scans)
    const scansState = useSelector((s) => s.guardian?.scans ?? s);
    const { list = [], count = 0, loading = false, error = null } = scansState;

    const { openScanId, open, close } = useOpenScan();
    const { filters, clearAll } = useScanFilters();
    const tokens = useTokens();

    const [selectedIndex, setSelectedIndex] = React.useState(0);

    // Determine whether all filters are at defaults
    const filtersAreDefault =
        (!filters.range || filters.range === '24h')
        && !filters.apiKeyIds?.length
        && !filters.actions?.length
        && !filters.categories?.length
        && !filters.correlationId
        && !filters.status
        && !filters.tenantId
        && !filters.userId
        && !filters.contentType;

    // Keyboard navigation
    const onKeyDown = (e) => {
        if (!list.length) return;
        if (e.key === 'ArrowDown' || e.key === 'j') {
            const next = Math.min(selectedIndex + 1, list.length - 1);
            setSelectedIndex(next);
            if (openScanId) open(list[next].correlation_id);
            e.preventDefault();
        } else if (e.key === 'ArrowUp' || e.key === 'k') {
            const prev = Math.max(selectedIndex - 1, 0);
            setSelectedIndex(prev);
            if (openScanId) open(list[prev].correlation_id);
            e.preventDefault();
        } else if (e.key === 'Enter') {
            open(list[selectedIndex].correlation_id);
            e.preventDefault();
        } else if (e.key === 'Escape') {
            close();
            e.preventDefault();
        }
    };

    // ---- Loading (first time) ----
    if (loading && !list.length) {
        return (
            <Stack data-testid="scan-list-skeleton" sx={{ gap: tokens.aliasesSpaceComponentDense }}>
                {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={44} />
                ))}
            </Stack>
        );
    }

    // ---- Error ----
    if (error) {
        return (
            <Card bdsType="staticSubtle" bdsPadding="default">
                <Alert bdsType="neutralError">{error}</Alert>
                <Box sx={{ marginTop: tokens.aliasesSpaceComponentDefault }}>
                    <Button
                        bdsType="interactiveNeutral"
                        onClick={() => dispatch(fetchScans({ filters }))}
                    >
                        Retry
                    </Button>
                </Box>
            </Card>
        );
    }

    // ---- Empty: no scans ever ----
    if (!list.length && filtersAreDefault) {
        return (
            <Card bdsType="staticSubtle" bdsPadding="spacious" sx={{ textAlign: 'center' }}>
                <Typography variant="body1Bold">No scans yet.</Typography>
                <Typography variant="body1" sx={{ marginTop: tokens.aliasesSpaceComponentDense }}>
                    Once your gateway starts processing requests, they'll appear here.
                </Typography>
            </Card>
        );
    }

    // ---- Empty: filters active ----
    if (!list.length) {
        return (
            <Card bdsType="staticSubtle" bdsPadding="spacious" sx={{ textAlign: 'center' }}>
                <Typography variant="body1">No scans match your filters.</Typography>
                <Box sx={{ marginTop: tokens.aliasesSpaceComponentDefault }}>
                    <Button bdsType="interactiveNeutral" onClick={clearAll}>
                        Clear all filters
                    </Button>
                </Box>
            </Card>
        );
    }

    // ---- Table ----
    return (
        <Box sx={{ opacity: loading ? 0.5 : 1 }}>
            {/* Using native table elements for full rendering control.
                BDS Table's headers+rows API doesn't support per-column custom renderers. */}
            <table
                tabIndex={0}
                onKeyDown={onKeyDown}
                aria-label="Scan history"
                style={{ width: '100%', borderCollapse: 'collapse' }}
            >
                <thead>
                    <tr>
                        {COLUMNS.map((c) => (
                            <th
                                key={c.key}
                                style={{
                                    textAlign: c.align || 'left',
                                    width: c.width,
                                    minWidth: c.minWidth,
                                    maxWidth: c.maxWidth,
                                    padding: `${tokens.aliasesSpaceContainerDense} ${tokens.aliasesSpaceComponentDefault}`,
                                    fontWeight: 600,
                                    borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
                                    color: tokens.aliasesColorTextStaticNeutral,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {list.map((row, i) => {
                        const ts = formatTimestamp(row.started_at);
                        const isOpen = openScanId === row.correlation_id;
                        const isSelected = i === selectedIndex;

                        return (
                            <tr
                                key={row.correlation_id}
                                onClick={() => {
                                    setSelectedIndex(i);
                                    open(row.correlation_id);
                                }}
                                style={{
                                    cursor: 'pointer',
                                    borderLeft: isOpen
                                        ? `3px solid ${tokens.aliasesColorBgInteractiveContainerEmphasisDefault}`
                                        : '3px solid transparent',
                                    backgroundColor: isOpen
                                        ? tokens.aliasesColorBgInteractiveContainerSubtleSelectedDefault
                                        : isSelected
                                            ? tokens.aliasesColorBgInteractiveContainerSubtleDefault
                                            : undefined,
                                    borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
                                }}
                            >
                                {/* Time */}
                                <td style={{ padding: `${tokens.aliasesSpaceContainerDense} ${tokens.aliasesSpaceComponentDefault}`, width: 150 }}>
                                    <Tooltip title={ts.full}>
                                        <Typography variant="captionNeutral" component="span">
                                            {ts.relative}
                                        </Typography>
                                    </Tooltip>
                                </td>

                                {/* Corr. ID */}
                                <td style={{ padding: `${tokens.aliasesSpaceContainerDense} ${tokens.aliasesSpaceComponentDefault}`, width: 160 }}>
                                    <Stack direction="row" sx={{ alignItems: 'center', gap: tokens.aliasesSpaceComponentDense, fontFamily: 'monospace' }}>
                                        <Typography variant="code" component="span">
                                            {row.correlation_id.slice(0, 8)}
                                        </Typography>
                                        <IconButton
                                            bdsSize="small"
                                            aria-label="Copy correlation ID"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (navigator.clipboard) {
                                                    navigator.clipboard.writeText(row.correlation_id);
                                                }
                                            }}
                                        >
                                            ⧉
                                        </IconButton>
                                    </Stack>
                                </td>

                                {/* API Key (account_name) */}
                                <td style={{
                                    padding: `${tokens.aliasesSpaceContainerDense} ${tokens.aliasesSpaceComponentDefault}`,
                                    minWidth: 100,
                                    maxWidth: 140,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    <Tooltip title={row.account_name || ''}>
                                        <Typography variant="captionNeutral" component="span">
                                            {row.account_name}
                                        </Typography>
                                    </Tooltip>
                                </td>

                                {/* Result */}
                                <td style={{ padding: `${tokens.aliasesSpaceContainerDense} ${tokens.aliasesSpaceComponentDefault}`, width: 90 }}>
                                    {row.result_action && (
                                        <Chip
                                            bdsType={RESULT_BDSTYPE[row.result_action] || 'staticNeutral'}
                                            bdsSize="medium"
                                            label={row.result_action}
                                            sx={{ width: 'fit-content' }}
                                        />
                                    )}
                                </td>

                                {/* Findings */}
                                <td style={{ padding: `${tokens.aliasesSpaceContainerDense} ${tokens.aliasesSpaceComponentDefault}`, minWidth: 240 }}>
                                    {row.finding_categories?.length ? (
                                        <Stack
                                            direction="row"
                                            sx={{
                                                gap: tokens.aliasesSpaceComponentDense,
                                                alignItems: 'flex-start',
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            {row.finding_categories.slice(0, 3).map((cat) => (
                                                <Chip
                                                    key={cat}
                                                    bdsType="staticNeutral"
                                                    bdsSize="medium"
                                                    label={CATEGORY_LABEL[cat] || cat}
                                                    sx={{ width: 'fit-content' }}
                                                />
                                            ))}
                                            {row.finding_categories.length > 3 && (
                                                <Chip
                                                    bdsType="staticNeutral"
                                                    bdsSize="medium"
                                                    label={`+${row.finding_categories.length - 3} more`}
                                                    sx={{ width: 'fit-content' }}
                                                />
                                            )}
                                        </Stack>
                                    ) : (
                                        <Typography variant="captionSubtle">—</Typography>
                                    )}
                                </td>

                                {/* Latency */}
                                <td style={{
                                    padding: `${tokens.aliasesSpaceContainerDense} ${tokens.aliasesSpaceComponentDefault}`,
                                    width: 80,
                                    textAlign: 'right',
                                }}>
                                    <Typography variant="captionNeutral">
                                        {humanizeMs(row.duration_ms ?? row.latency_ms)}
                                    </Typography>
                                </td>

                                {/* Content type */}
                                <td style={{ padding: `${tokens.aliasesSpaceContainerDense} ${tokens.aliasesSpaceComponentDefault}`, width: 120 }}>
                                    <Typography variant="captionSubtle">
                                        {row.content_type}
                                    </Typography>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Task 20: Pagination footer */}
            {count > 0 && <PaginationFooter count={count} />}
        </Box>
    );
}
